// src/data/shortlist.js
const STORAGE_KEY = "exodia_shortlist_v1";
const API_BASE_URL = "https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function";

// --- helpers ---
const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const write = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    // Notify same-tab listeners
    window.dispatchEvent(new CustomEvent("exodia:shortlist-changed"));
  } catch {}
};

const normalize = (t) => {
  // Keep only the essentials we need to render everywhere
  const id = String(t?.talentId ?? t?.id ?? t?.ROWID ?? "");
  return {
    talentId: id,
    name: t?.name ?? t?.alias ?? "—",
    alias: t?.alias ?? t?.name ?? "—",
    role: t?.role ?? "—",
    stage: t?.stage || 'Shortlist',
    status: t?.status || ' ',
    avatar: t?.avatars?.Senior || t?.avatar || null,
  };
};

// --- API functions ---
export async function getShortlist(userId) {
  if (!userId) return read(); // Fallback to localStorage
  
  try {
    // Get shortlisted talents from the Shortlist table
    const response = await fetch(`${API_BASE_URL}/shortlist?userId=${encodeURIComponent(userId)}`);
    const result = await response.json();
    return result.status === 'success' ? result.data : [];
  } catch (error) {
    console.error('Failed to get shortlist from API:', error);
    return read(); // Fallback to localStorage
  }
}

export async function getAllShortlistedTalentIds() {
  try {
    const response = await fetch(`${API_BASE_URL}/shortlist?all=true`);
    const result = await response.json();
    return result.status === 'success' ? result.data : [];
  } catch (error) {
    console.error('Failed to get all shortlisted talent IDs:', error);
    return [];
  }
}

export async function isShortlisted(id, userId) {
  if (!userId) {
    // Fallback to localStorage check
    const key = String(id);
    return read().some((x) => String(x.talentId) === key);
  }
  
  try {
    const shortlist = await getShortlist(userId);
    return shortlist.some((x) => String(x.talent_id) === String(id));
  } catch (error) {
    console.error('Failed to check shortlist status:', error);
    return false;
  }
}

export async function addToShortlist(talent, userId) {
  if (!userId) {
    // Fallback to localStorage
    const next = normalize(talent);
    if (!next.talentId) return false;
    const list = read();
    if (list.some((x) => String(x.talentId) === String(next.talentId))) return false;
    write([...list, next]);
    return true;
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/shortlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        talent_id: String(talent.talentId ?? talent.id ?? talent.ROWID ?? ""),
        talent_name: talent.name ?? talent.alias ?? "—",
        talent_role: talent.role ?? "—",
      }),
    });
    
    const result = await response.json();
    if (result.status === 'success') {
      // Update localStorage as backup
      const next = normalize(talent);
      if (next.talentId) {
        const list = read();
        if (!list.some((x) => String(x.talentId) === String(next.talentId))) {
          write([...list, next]);
        }
      }
      // The backend now returns remaining_shortlist_passes in the response
      if (result.data?.remaining_shortlist_passes !== undefined) {
        console.log(`Shortlist pass used. Remaining passes: ${result.data.remaining_shortlist_passes}`);
      }
      return true;
    } else if (result.status === 'error') {
      // Handle specific error messages from backend
      if (result.message.includes('No shortlist passes remaining')) {
        alert('No shortlist passes remaining. Please purchase more shortlist passes to continue.');
      } else {
        alert(result.message || 'Failed to add to shortlist');
      }
    }
    return false;
  } catch (error) {
    console.error('Failed to add to shortlist:', error);
    return false;
  }
}

export async function removeFromShortlist(id, userId) {
  if (!userId) {
    // Fallback to localStorage
    const key = String(id);
    const list = read().filter((x) => String(x.talentId) !== key);
    write(list);
    return true;
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/shortlist`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        talent_id: String(id),
      }),
    });
    
    const result = await response.json();
    if (result.status === 'success') {
      // Update localStorage as backup
      const key = String(id);
      const list = read().filter((x) => String(x.talentId) !== key);
      write(list);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to remove from shortlist:', error);
    return false;
  }
}

export function clearShortlist() {
  write([]);
}

// Default status for each stage
const DEFAULT_STATUS_FOR_STAGE = {
  'Shortlist': null,
  'Pre-Onboarding': 'Talent Sourcing & Pooling',
  'Onboarding': 'Background Checks & Verification',
  'Deployment': 'Training',
  'Offboarding': 'Pre-Offboarding'
};

export async function updateShortlistStage(id, userId, stage, status = null) {
  if (!userId || !id) return;
  
  // Set default status if not provided
  const finalStatus = status || DEFAULT_STATUS_FOR_STAGE[stage] || null;
  
  const payload = { 
    user_id: userId, 
    talent_id: String(id), 
    stage: stage,
    status: finalStatus
  };
  
  console.log('Updating shortlist stage with payload:', payload);
  
  try {
    const response = await fetch(`${API_BASE_URL}/shortlist`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    
    console.log('Response status:', response.status);
    const result = await response.json();
    console.log('Response data:', result);
    
    if (result.status !== 'success') {
      throw new Error(result.message || 'Failed to update shortlist stage');
    }
    
    return result;
  } catch (error) {
    console.error('Failed to update shortlist stage:', error);
    throw error;
  }
}

// New function to update status within a stage
export async function updateShortlistStatus(id, userId, status) {
  if (!userId || !id || !status) return;
  
  const payload = { 
    user_id: userId, 
    talent_id: String(id), 
    status: status
  };
  
  console.log('Updating shortlist status with payload:', payload);
  
  try {
    const response = await fetch(`${API_BASE_URL}/shortlist`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    
    console.log('Response status:', response.status);
    const result = await response.json();
    console.log('Response data:', result);
    
    if (result.status !== 'success') {
      throw new Error(result.message || 'Failed to update shortlist status');
    }
    
    return result;
  } catch (error) {
    console.error('Failed to update shortlist status:', error);
    throw error;
  }
}
